import { useState, useEffect, useCallback } from 'react'
import { messageAPI } from '../../../services/api'

interface UseMessageNotificationOptions {
  projectId?: string
  pollingInterval?: number
  enablePolling?: boolean
}

interface UseMessageNotificationReturn {
  unreadCount: number
  totalUnreadCount: number
  loading: boolean
  error: string | null
  refreshUnreadCount: () => Promise<void>
  refreshTotalUnreadCount: () => Promise<void>
}

export function useMessageNotification({
  projectId,
  pollingInterval = 30000,
  enablePolling = true,
}: UseMessageNotificationOptions = {}): UseMessageNotificationReturn {
  const [unreadCount, setUnreadCount] = useState(0)
  const [totalUnreadCount, setTotalUnreadCount] = useState(0)
  const [loading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refreshUnreadCount = useCallback(async () => {
    if (!projectId) return

    try {
      setError(null)
      const response = await messageAPI.getUnreadCounts({ projectId })
      setUnreadCount(response.data.unread_count || 0)
    } catch (error) {
      console.error('Error loading unread count:', error)
      // Silent fail for unread count
    }
  }, [projectId])

  const refreshTotalUnreadCount = useCallback(async () => {
    try {
      setError(null)
      const response = await messageAPI.getUnreadCounts({ includeTotal: true })
      setTotalUnreadCount(response.data.unread_count || 0)
    } catch (error) {
      console.error('Error loading total unread count:', error)
      // Silent fail for total unread count
    }
  }, [])

  // Load initial data
  useEffect(() => {
    if (projectId) {
      refreshUnreadCount()
    }
    refreshTotalUnreadCount()
  }, [projectId, refreshUnreadCount, refreshTotalUnreadCount])

  // Setup polling
  useEffect(() => {
    if (!enablePolling) return

    const interval = setInterval(() => {
      if (projectId) {
        refreshUnreadCount()
      }
      refreshTotalUnreadCount()
    }, pollingInterval)

    return () => clearInterval(interval)
  }, [enablePolling, pollingInterval, projectId, refreshUnreadCount, refreshTotalUnreadCount])

  return {
    unreadCount,
    totalUnreadCount,
    loading,
    error,
    refreshUnreadCount,
    refreshTotalUnreadCount,
  }
}
